import React, { useState, useEffect } from 'react';
import {
  FiArrowLeft,
  FiShare2,
  FiBookOpen,
  FiEdit3,
  FiMapPin,
  FiVolumeX,
  FiRefreshCw,
  FiCheck,
  FiAlertTriangle,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import GetStartedWith from '../../../components/GetStartedWith';
import './WritingResults.css';
import {
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  getAuthHeaders,
} from '../../../utils/utils';
import {
  getCEFRFromIELTS,
  CEFR_ORDER,
} from '../../../utils/cefrUtils';
import ReactMarkdown from 'react-markdown';
import { sanitizeHtml } from '../../../utils/safeHtml';
import ToeflNextStepCard from '../toefl/ToeflNextStepCard';

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

const setScopedLocalStorageItem = (key, value) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  if (!scopedKey) return false;
  localStorage.setItem(scopedKey, value);
  return true;
};

export default function WritingResults({ testResults, testData }) {
  const navigate = useNavigate();
  const { testId } = useParams();
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(0);
  const [allAttempts, setAllAttempts] = useState([]);
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(0);
  const [, setError] = useState(null); // eslint-disable-line no-unused-vars
  const [, setRequestingHumanAssessment] = useState(false); // eslint-disable-line no-unused-vars
  const [, setHumanAssessmentRequested] = useState(false); // eslint-disable-line no-unused-vars

  // Helper function to map backend result to frontend structure
  const mapBackendResult = (backendResult) => ({
    testId: backendResult.test_id,
    testType: backendResult.test_type,
    userEmail: backendResult.user_email,
    completedAt: backendResult.created_at,
    startTime: backendResult.start_time,
    endTime: backendResult.end_time,
    timeTakenSeconds: backendResult.time_taken_seconds,
    essays: backendResult.essays || {},
    taskData: backendResult.task_data || {},
    assessmentData: backendResult.assessment_data || null,
    totalWords: backendResult.total_words || 0,
    attemptId: backendResult.id, // Store the backend ID for reference
  });

  // Helper function to parse score strings like "5 / 40" to just the number
  const parseScore = (scoreStr) => {
    if (typeof scoreStr === 'string') {
      // Handle both "5 / 40" and "5" formats
      if (scoreStr.includes(' / ')) {
        return parseFloat(scoreStr.split(' / ')[0]);
      }
      return parseFloat(scoreStr);
    }
    return parseFloat(scoreStr) || 0;
  };

  // Helper function to parse band score strings like "2.0 / 9.0" to just the number
  const parseBandScore = (bandStr) => {
    if (typeof bandStr === 'string') {
      // Handle both "2.0 / 9.0" and "2.0" formats
      if (bandStr.includes(' / ')) {
        return parseFloat(bandStr.split(' / ')[0]);
      }
      return parseFloat(bandStr);
    }
    return parseFloat(bandStr) || 0.0;
  };

  useEffect(() => {
    const fetchWritingResults = async () => {
      try {
        setLoading(true);

        // First try to get results from backend
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/get-writing-test-results?testType=writing&testId=${testId}`,
            { headers: getAuthHeaders() }
          );

          if (response.ok) {
            const backendData = await response.json();
            console.log('Backend writing results:', backendData);

            if (
              backendData.success &&
              backendData.results &&
              backendData.results.length > 0
            ) {
              // Map all attempts to our expected structure
              const mappedAttempts = backendData.results.map((result) =>
                mapBackendResult(result)
              );
              setAllAttempts(mappedAttempts);

              // Use the latest attempt (first in array) as default
              const latestAttempt = mappedAttempts[0];
              setSelectedAttemptIndex(0);

              // Build assessment data from latest attempt + original test data
              await buildAssessmentData(latestAttempt, testData);

              setLoading(false);
              return;
            }
          }
        } catch (backendError) {
          console.log(
            'Backend results not found, checking localStorage...',
            backendError
          );
        }

        // Fallback to localStorage for recently completed tests
        const completedTests = getScopedLocalStorageItem('completedTests');
        if (completedTests) {
          const parsed = JSON.parse(completedTests);
          const typeTests = parsed.writing || [];
          const completedTest = typeTests.find(
            (t) => t.testId === parseInt(testId)
          );

          if (completedTest) {
            setAllAttempts([completedTest]);
            setSelectedAttemptIndex(0);
            await buildAssessmentData(completedTest, testData);
          } else {
            setError(
              'No results found for this test. Please complete the test first.'
            );
          }
        } else {
          setError(
            'No results found for this test. Please complete the test first.'
          );
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching writing results:', err);
        setError('Error loading test results. Please try again.');
        setLoading(false);
      }
    };

    if (testId && testData) {
      fetchWritingResults();
    }
  }, [testId, testData]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildAssessmentData = async (attemptData, originalTestData) => {
    try {
      // Build tasks array from original test data + attempt assessment data
      const tasksWithData = originalTestData.sections.map((section, index) => {
        const taskNumber = index + 1;
        const taskKey = `task${taskNumber}`;
        let backendTask = null;

        // Check for assessment data in the attempt
        if (attemptData.assessmentData && attemptData.assessmentData[taskKey]) {
          backendTask = attemptData.assessmentData[taskKey];
        }

        // Get real essay data from attempt, with fallbacks
        const essayFromPart = attemptData.essays?.[`part${taskNumber}`]?.essay;
        const essayFromTask = attemptData.taskData?.[taskKey]?.essay;
        const realEssay = essayFromPart || essayFromTask || '';

        const wordCountFromPart =
          attemptData.essays?.[`part${taskNumber}`]?.wordCount;
        const wordCountFromTask = attemptData.taskData?.[taskKey]?.wordCount;
        const realWordCount = wordCountFromPart || wordCountFromTask || 0;

        // Get section title with fallback
        const sectionTitle =
          attemptData.essays?.[`part${taskNumber}`]?.sectionTitle ||
          `Task ${taskNumber}`;

        // Get instructions with fallback
        const instructions =
          attemptData.essays?.[`part${taskNumber}`]?.instructions ||
          section.instructions ||
          `Task ${taskNumber} Instructions`;

        return {
          taskNumber: taskNumber,
          sectionTitle: sectionTitle,
          instructions: instructions,
          question: section.question || {},
          userEssay: realEssay,
          wordCount: realWordCount,
          minWords:
            attemptData.essays?.[`part${taskNumber}`]?.minWords ||
            section.minWords ||
            0,
          timeAllocated:
            attemptData.essays?.[`part${taskNumber}`]?.timeAllocated ||
            section.timeInMinutes ||
            0,
          // Real backend scores if available
          scores: {
            taskFulfilment: backendTask?.assessment
              ? parseScore(backendTask.assessment.task_fulfillment_score)
              : 0,
            organization: backendTask?.assessment
              ? parseScore(backendTask.assessment.organization_score)
              : 0,
            coherenceCohesion: backendTask?.assessment
              ? parseScore(backendTask.assessment.coherence_cohesion_score)
              : 0,
            languageUse: backendTask?.assessment
              ? parseScore(backendTask.assessment.language_use_score)
              : 0,
          },
          estimatedRawScore: backendTask?.assessment
            ? parseScore(backendTask.assessment.estimated_raw_score)
            : 0,
          estimatedBandScore: backendTask?.assessment
            ? parseBandScore(backendTask.assessment.estimated_band_score)
            : 0.0,
          resultId: backendTask?.result_id || null,
          // Additional backend data
          insights: {
            taskCompleteness:
              backendTask?.assessment?.task_completeness_insights || '',
            essayStructure:
              backendTask?.assessment?.essay_structure_insights || '',
            grammar: backendTask?.assessment?.grammar_insights || '',
          },
          suggestedMindMap: backendTask?.assessment?.suggested_mind_map || null,
          suggestedWords: backendTask?.assessment?.suggested_words || [],
          alternativeAnswer: backendTask?.assessment?.alternative_answer || '',
          spellingVariant:
            backendTask?.assessment?.spellingVariant ||
            backendTask?.spellingVariant ||
            null,
        };
      });

      // Calculate overall band score with fallback
      let overallBandScore = 0;
      if (attemptData.assessmentData?.overallBandScore) {
        overallBandScore = parseBandScore(
          attemptData.assessmentData.overallBandScore
        );
      } else {
        // Calculate from individual task scores
        const task1Score = tasksWithData[0]?.estimatedBandScore || 0;
        const task2Score = tasksWithData[1]?.estimatedBandScore || 0;
        overallBandScore = (task1Score + task2Score) / 2;
      }

      const combinedData = {
        testId: attemptData.testId,
        userEmail: attemptData.userEmail || 'user@example.com',
        completedAt: attemptData.completedAt,
        overallBandScore: overallBandScore,
        totalWords:
          attemptData.totalWords ||
          tasksWithData.reduce((sum, task) => sum + task.wordCount, 0),
        tasks: tasksWithData,
        attemptId: attemptData.attemptId || null,
      };

      setAssessmentData(combinedData);
    } catch (error) {
      console.error('Error building assessment data:', error);
      setError('Error processing assessment data.');
    }
  };

  const handleAttemptChange = async (index) => {
    setSelectedAttemptIndex(index);
    setLoading(true);

    const selectedAttempt = allAttempts[index];
    await buildAssessmentData(selectedAttempt, testData);

    setLoading(false);
  };

  const handleShare = () => {
    const shareText = `Check out my IELTS Writing Test results! Overall Band Score: ${assessmentData.overallBandScore}/9`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      navigator
        .share({
          title: 'IELTS Writing Test Results',
          text: shareText,
          url: shareUrl,
        })
        .catch(console.error);
    } else {
      const shareContent = `${shareText}\n${shareUrl}`;
      navigator.clipboard.writeText(shareContent).then(() => {
        alert('Link copied to clipboard!');
      });
    }
  };

  const handleRetakeTest = () => {
    const examInfo = {
      id: testData?.id || parseInt(testId),
      title: testData?.title || `Writing Test ${testId}`,
      type: 'writing',
      startTime: new Date().toISOString(),
    };
    setScopedLocalStorageItem('englishExam', JSON.stringify(examInfo));
    navigate('/exams/english/writing');
  };

  const handleRequestHumanAssessment = async () => { // eslint-disable-line no-unused-vars
    try {
      setRequestingHumanAssessment(true);

      const user = JSON.parse(sessionStorage.getItem('user'));
      const selectedAttempt = allAttempts[selectedAttemptIndex];

      const requestPayload = {
        testId: testId,
        testType: 'writing',
        userEmail: user.email,
        attemptId: selectedAttempt.attemptId,
        completedAt: selectedAttempt.completedAt,
        essays: selectedAttempt.essays,
        taskData: selectedAttempt.taskData,
        requestedAt: new Date().toISOString(),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/request-human-assessment`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(requestPayload),
        }
      );

      if (response.ok) {
        await response.json();
        setHumanAssessmentRequested(true);

        // Show success message
        alert(
          'Human assessment request submitted successfully! Our IELTS expert will review your essays and provide detailed feedback through your messaging box or email within 24-48 hours.'
        );
      } else {
        throw new Error('Failed to request human assessment');
      }
    } catch (error) {
      console.error('Error requesting human assessment:', error);
      alert('Failed to request human assessment. Please try again later.');
    } finally {
      setRequestingHumanAssessment(false);
    }
  };

  const renderScoreCard = (task) => (
    <div className="writing-task-score-card">
      <h3>
        Scores{' '}
        {task.resultId && (
          <span
            style={{ fontSize: '14px', color: '#4CAF50', fontWeight: 'normal' }}
          >
            (Estimated Assessment)
          </span>
        )}
      </h3>
      <div className="writing-score-grid">
        <div className="writing-score-item">
          <span className="writing-score-label">Task Fulfilment</span>
          <span className="writing-score-value">
            {task.scores.taskFulfilment}/40
          </span>
        </div>
        <div className="writing-score-item">
          <span className="writing-score-label">Organization</span>
          <span className="writing-score-value">
            {task.scores.organization}/40
          </span>
        </div>
        <div className="writing-score-item">
          <span className="writing-score-label">Coherence and Cohesion</span>
          <span className="writing-score-value">
            {task.scores.coherenceCohesion}/40
          </span>
        </div>
        <div className="writing-score-item">
          <span className="writing-score-label">Language Use</span>
          <span className="writing-score-value">
            {task.scores.languageUse}/40
          </span>
        </div>
      </div>
      <div className="writing-overall-scores">
        <div className="writing-overall-score-item">
          <span className="writing-score-label">
            Estimated Overall Raw Score
          </span>
          <span className="writing-score-value">
            {task.estimatedRawScore}/160
          </span>
        </div>
        <div className="writing-overall-score-item">
          <span className="writing-score-label">
            Estimated Overall Band Score
          </span>
          <span className="writing-score-value writing-band-score">
            {task.estimatedBandScore.toFixed(1)}/9
            <span
              className="writing-task-cefr-badge"
              style={{
                background: getCEFRFromIELTS(task.estimatedBandScore).color,
              }}
            >
              {getCEFRFromIELTS(task.estimatedBandScore).code}
            </span>
          </span>
        </div>
      </div>
      {task.resultId && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: '#e8f5e8',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#1b5e20',
          }}
        >
          <FiCheck style={{ color: '#4caf50', marginRight: '0.5rem' }} /> This
          assessment was generated by our AI evaluation system (Result ID:{' '}
          {task.resultId})
        </div>
      )}
      {!task.resultId && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: '#fff3e0',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#e65100',
          }}
        >
          <FiAlertTriangle
            style={{ color: '#ff9800', marginRight: '0.5rem' }}
          />{' '}
          Assessment pending - scores will be available after AI evaluation
        </div>
      )}
    </div>
  );

  const renderSpellingMixBanner = (task) => {
    const variant = task?.spellingVariant;
    if (!variant || variant.variant !== 'mixed') return null;
    const pairs = [
      ...new Set((variant.pairs || []).map((item) => `${item.british} / ${item.american}`)),
    ];
    return (
      <div
        data-testid="spelling-mix-banner"
        style={{
          margin: '0 0 1rem',
          padding: '0.85rem 1rem',
          background: '#fff8e6',
          border: '1px solid #f0d48a',
          borderRadius: 8,
        }}
      >
        <p style={{ margin: '0 0 0.4rem' }}>
          You mixed British and American spelling
          {pairs.length ? ` (${pairs.join(', ')})` : ''}. IELTS accepts either
          system. Pick one and use it for the whole test.
        </p>
        <a href="/exams/english/vocabulary/spelling-us-uk">Open the US vs UK spelling lesson</a>
      </div>
    );
  };

  const renderBasicInsights = (task) => (
    <div className="writing-insights">
      {renderSpellingMixBanner(task)}
      <div className="writing-insight-section">
        <h4>
          <FiBookOpen /> Task Performance
        </h4>
        <div className="writing-insight-score">
          {task.resultId ? 'AI Assessment Complete' : 'Assessment Pending'}
        </div>
        <ul className="writing-insight-list">
          <li
            className={task.wordCount >= task.minWords ? 'positive' : 'warning'}
          >
            {task.wordCount >= task.minWords ? (
              <FiCheck style={{ color: '#4caf50' }} />
            ) : (
              <FiAlertTriangle style={{ color: '#ff9800' }} />
            )}{' '}
            Word count: {task.wordCount} words
            {task.minWords > 0 && ` (minimum: ${task.minWords})`}
          </li>
          <li className={task.userEssay.length > 0 ? 'positive' : 'warning'}>
            {task.userEssay.length > 0 ? (
              <FiCheck style={{ color: '#4caf50' }} />
            ) : (
              <FiAlertTriangle style={{ color: '#ff9800' }} />
            )}{' '}
            Essay completion:{' '}
            {task.userEssay.length > 0 ? 'Complete' : 'Incomplete'}
          </li>
          {task.resultId && (
            <>
              <li
                className={
                  task.scores.taskFulfilment > 5 ? 'positive' : 'warning'
                }
              >
                {task.scores.taskFulfilment > 5 ? (
                  <FiCheck style={{ color: '#4caf50' }} />
                ) : (
                  <FiAlertTriangle style={{ color: '#ff9800' }} />
                )}{' '}
                Task fulfilment: {task.scores.taskFulfilment}/40
              </li>
              <li
                className={
                  task.scores.organization > 5 ? 'positive' : 'warning'
                }
              >
                {task.scores.organization > 5 ? (
                  <FiCheck style={{ color: '#4caf50' }} />
                ) : (
                  <FiAlertTriangle style={{ color: '#ff9800' }} />
                )}{' '}
                Organization: {task.scores.organization}/40
              </li>
            </>
          )}
        </ul>
      </div>

      {task.resultId && task.insights && (
        <>
          {/* Task Completeness Insights */}
          {task.insights.taskCompleteness && (
            <div className="writing-insight-section">
              <h4>
                <FiEdit3 /> Task Completeness Analysis
              </h4>
              <div className="writing-insight-content">
                <ReactMarkdown>{task.insights.taskCompleteness}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Essay Structure Insights */}
          {task.insights.essayStructure && (
            <div className="writing-insight-section">
              <h4>
                <FiMapPin /> Essay Structure Analysis
              </h4>
              <div className="writing-insight-content">
                <ReactMarkdown>{task.insights.essayStructure}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Grammar and Language Insights */}
          {task.insights.grammar && (
            <div className="writing-insight-section">
              <h4>
                <FiVolumeX /> Grammar & Language Analysis
              </h4>
              <div className="writing-insight-content">
                <ReactMarkdown>{task.insights.grammar}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Suggested Words */}
          {task.suggestedWords && task.suggestedWords.length > 0 && (
            <div className="writing-insight-section">
              <h4>
                <FiBookOpen /> Suggested Vocabulary
              </h4>
              <div className="writing-vocabulary-suggestions">
                {task.suggestedWords.map((wordData, index) => (
                  <div key={index} className="writing-vocab-card">
                    <div className="writing-vocab-word">
                      <strong>{wordData.word}</strong>
                      <span className="writing-phonetic">
                        {wordData.phonetic}
                      </span>
                      <span className="writing-part-of-speech">
                        {wordData.part_of_speech}
                      </span>
                    </div>
                    <div className="writing-vocab-definition">
                      {wordData.definition}
                    </div>
                    <div className="writing-vocab-example">
                      <em>Example: {wordData.sample_sentence}</em>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mind Map */}
          {task.suggestedMindMap && (
            <div className="writing-insight-section">
              <h4>
                <FiMapPin /> Suggested Essay Structure
              </h4>
              <div className="writing-mind-map">
                {Object.entries(task.suggestedMindMap).map(
                  ([section, points]) => {
                    const pointsList = Array.isArray(points) ? points : [points];
                    return (
                      <div key={section} className="writing-mind-map-section">
                        <h5>
                          {section.replace(/_/g, ' ').charAt(0).toUpperCase() + section.replace(/_/g, ' ').slice(1)}
                        </h5>
                        <ul>
                          {pointsList.map((point, index) => (
                            <li key={index}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}

          {/* Alternative Answer */}
          {task.alternativeAnswer && (
            <div className="writing-insight-section writing-alternative-answer-section">
              <h4>
                <FiEdit3 /> Example Model Answer (Generated by Liwaza AI
                Copilot)
              </h4>
              <div className="writing-alternative-answer">
                <ReactMarkdown>{task.alternativeAnswer}</ReactMarkdown>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="english-layout">
        <Sidebar />
        <main className="english-main">
          <div className="writing-results-container">
            <div className="writing-loading-state">
              <div className="writing-loading-spinner"></div>
              <p>Loading your writing assessment...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!assessmentData) {
    return (
      <div className="english-layout">
        <Sidebar />
        <main className="english-main">
          <div className="writing-results-container">
            <div className="writing-error-state">
              <h2>Assessment Not Found</h2>
              <p>We couldn't find the assessment data for this test.</p>
              <button
                className="writing-btn-primary"
                onClick={() =>
                  navigate('/exams/english', {
                    state: { activeCategory: 'writing' },
                  })
                }
              >
                <FiArrowLeft /> Back to Writing Tests
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const currentTask = assessmentData.tasks[activeTask];

  return (
    <div className="english-layout">
      <Sidebar />
      <main className="english-main">
        <div className="writing-results-container">
          {/* Header Card with left-right layout */}
          <div className="writing-header-card">
            <div className="writing-card-content">
              {/* Back Button */}
              <div className="writing-header-controls">
                <button
                  onClick={() => navigate(-1)}
                  className="writing-back-button small"
                >
                  <FiArrowLeft />
                  <span>Back</span>
                </button>
              </div>

              <h1 className="writing-card-title">
                <FiEdit3 style={{ marginRight: '0.5rem' }} /> IELTS Writing
                Results
              </h1>

              {/* Main Content - Left/Right Layout */}
              <div className="writing-card-main-content">
                {/* Left Section - Controls */}
                <div className="writing-card-left-section">
                  <div className="writing-test-info">
                    <h2 className="writing-test-title">
                      Writing Test Assessment
                    </h2>
                    <div className="writing-test-meta">
                      <span>
                        Completed:{' '}
                        {new Date(
                          assessmentData.completedAt
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="writing-card-controls">
                    {/* Attempt Selector */}
                    {allAttempts.length > 1 && (
                      <div className="writing-card-attempt-selector">
                        <label className="writing-attempt-label">
                          Test Attempt:
                        </label>
                        <select
                          value={selectedAttemptIndex}
                          onChange={(e) =>
                            handleAttemptChange(parseInt(e.target.value))
                          }
                          className="writing-card-attempt-select"
                        >
                          {allAttempts.map((attempt, index) => {
                            // Calculate band score for display
                            let displayBandScore = 'N/A';
                            if (attempt.assessmentData?.overallBandScore) {
                              displayBandScore = parseBandScore(
                                attempt.assessmentData.overallBandScore
                              ).toFixed(1);
                            } else if (
                              attempt.assessmentData?.task1?.assessment &&
                              attempt.assessmentData?.task2?.assessment
                            ) {
                              const task1Score = parseBandScore(
                                attempt.assessmentData.task1.assessment
                                  .estimated_band_score
                              );
                              const task2Score = parseBandScore(
                                attempt.assessmentData.task2.assessment
                                  .estimated_band_score
                              );
                              displayBandScore = (
                                (task1Score + task2Score) /
                                2
                              ).toFixed(1);
                            }

                            return (
                              <option key={index} value={index}>
                                Attempt {allAttempts.length - index} -{' '}
                                {new Date(
                                  attempt.completedAt
                                ).toLocaleDateString()}
                                {displayBandScore !== 'N/A' &&
                                  ` (${displayBandScore}/9)`}
                              </option>
                            );
                          })}
                        </select>
                        <div className="writing-card-attempt-badge">
                          {selectedAttemptIndex === 0 ? 'Latest' : 'Previous'}
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <div className="writing-results-action-buttons">
                      <button
                        onClick={handleRetakeTest}
                        className="writing-card-retake-button writing-btn-primary"
                      >
                        <FiRefreshCw />
                        <span>Retake Test</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Section - Overall Score */}
                <div className="writing-card-right-section">
                  {(() => {
                    const cefrLevel = getCEFRFromIELTS(
                      assessmentData.overallBandScore
                    );
                    const getUserDiagnosedLevel = () => {
                      try {
                        const user = JSON.parse(
                          sessionStorage.getItem('user') || '{}'
                        );
                        if (user?.email) {
                          const journeyData = localStorage.getItem(
                            `languageJourney_${user.email}`
                          );
                          if (journeyData) {
                            const parsed = JSON.parse(journeyData);
                            return parsed.currentLevel || 'B1';
                          }
                        }
                        return 'B1';
                      } catch {
                        return 'B1';
                      }
                    };
                    const userLevel = getUserDiagnosedLevel();
                    const testLevelIndex = CEFR_ORDER.indexOf(cefrLevel.code);
                    const userLevelIndex = CEFR_ORDER.indexOf(userLevel);
                    const performanceClass =
                      testLevelIndex >= userLevelIndex
                        ? 'excellent'
                        : testLevelIndex === userLevelIndex - 1
                          ? 'good'
                          : 'needs-work';

                    return (
                      <div
                        className={`writing-score-card writing-score-card-${performanceClass}`}
                      >
                        <h3>Overall Band Score</h3>
                        <div
                          className={`writing-score-display ${
                            assessmentData.overallBandScore >= 7
                              ? 'writing-score-excellent'
                              : assessmentData.overallBandScore >= 5.5
                                ? 'writing-score-good'
                                : 'writing-score-needs-improvement'
                          }`}
                        >
                          {assessmentData.overallBandScore.toFixed(1)}
                          <span className="writing-score-denominator">/9</span>
                        </div>
                        <div
                          className="writing-cefr-level-badge"
                          style={{ background: cefrLevel.color }}
                        >
                          {cefrLevel.code} · {cefrLevel.name}
                        </div>
                        <div className="writing-score-description">
                          {assessmentData.overallBandScore >= 7
                            ? 'Excellent performance!'
                            : assessmentData.overallBandScore >= 5.5
                              ? 'Good foundation to build on!'
                              : "Keep practicing - you're improving!"}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          <ToeflNextStepCard bandScore={assessmentData?.overallBandScore} />

          {/* Progress Comparison (only show for previous attempts) */}
          {allAttempts.length > 1 && selectedAttemptIndex > 0 && (
            <div className="writing-progress-comparison">
              <h3>Progress Comparison</h3>
              {(() => {
                const currentAttempt = allAttempts[selectedAttemptIndex];
                const latestAttempt = allAttempts[0];

                // Calculate current attempt score
                let currentScore = 0;
                if (currentAttempt.assessmentData?.overallBandScore) {
                  currentScore = parseBandScore(
                    currentAttempt.assessmentData.overallBandScore
                  );
                } else if (
                  currentAttempt.assessmentData?.task1?.assessment &&
                  currentAttempt.assessmentData?.task2?.assessment
                ) {
                  const task1Score = parseBandScore(
                    currentAttempt.assessmentData.task1.assessment
                      .estimated_band_score
                  );
                  const task2Score = parseBandScore(
                    currentAttempt.assessmentData.task2.assessment
                      .estimated_band_score
                  );
                  currentScore = (task1Score + task2Score) / 2;
                }

                // Calculate latest attempt score
                let latestScore = 0;
                if (latestAttempt.assessmentData?.overallBandScore) {
                  latestScore = parseBandScore(
                    latestAttempt.assessmentData.overallBandScore
                  );
                } else if (
                  latestAttempt.assessmentData?.task1?.assessment &&
                  latestAttempt.assessmentData?.task2?.assessment
                ) {
                  const task1Score = parseBandScore(
                    latestAttempt.assessmentData.task1.assessment
                      .estimated_band_score
                  );
                  const task2Score = parseBandScore(
                    latestAttempt.assessmentData.task2.assessment
                      .estimated_band_score
                  );
                  latestScore = (task1Score + task2Score) / 2;
                }

                const scoreDiff = latestScore - currentScore;
                const wordsDiff =
                  (latestAttempt.totalWords || 0) -
                  (currentAttempt.totalWords || 0);

                return (
                  <div className="writing-comparison-grid">
                    <div className="writing-comparison-item">
                      <div className="writing-comparison-label">
                        Band Score Change
                      </div>
                      <div
                        className={`writing-comparison-value ${scoreDiff > 0 ? 'positive' : scoreDiff < 0 ? 'negative' : 'neutral'}`}
                      >
                        {scoreDiff > 0 ? '+' : ''}
                        {scoreDiff.toFixed(1)}
                      </div>
                    </div>
                    <div className="writing-comparison-item">
                      <div className="writing-comparison-label">
                        Word Count Change
                      </div>
                      <div
                        className={`writing-comparison-value ${wordsDiff > 0 ? 'positive' : wordsDiff < 0 ? 'negative' : 'neutral'}`}
                      >
                        {wordsDiff > 0 ? '+' : ''}
                        {wordsDiff}
                      </div>
                    </div>
                    <div className="writing-comparison-item">
                      <div className="writing-comparison-label">
                        Latest Attempt
                      </div>
                      <div className="writing-comparison-value neutral">
                        {new Date(
                          latestAttempt.completedAt
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Task Navigation */}
          <div className="writing-task-navigation">
            {assessmentData.tasks.map((task, index) => (
              <button
                key={index}
                className={`writing-task-nav-btn ${activeTask === index ? 'active' : ''}`}
                onClick={() => setActiveTask(index)}
              >
                {task.sectionTitle}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div className="writing-results-content">
            {/* Task Instructions and Question */}
            <section className="writing-task-section">
              <h2>{currentTask.sectionTitle}</h2>
              <div className="writing-task-instructions">
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(currentTask.instructions),
                  }}
                />
                <div className="writing-question-content">
                  <p>{currentTask.question.description}</p>
                  {currentTask.question.type === 'image' && (
                    <div className="writing-question-image">
                      <img
                        src={currentTask.question.imageUrl}
                        alt="Task prompt"
                      />
                    </div>
                  )}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(currentTask.question.task),
                    }}
                  />
                </div>
              </div>
            </section>

            {/* User's Essay */}
            <section className="writing-essay-section">
              <h3>Your Essay ({currentTask.wordCount} words)</h3>
              <div className="writing-user-essay">
                {currentTask.userEssay.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </section>

            {/* Assessment Results */}
            {renderScoreCard(currentTask)}
            {renderBasicInsights(currentTask)}
          </div>

          {/* Get Started With Component */}
          <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
            <GetStartedWith
              title="Need help improving your IELTS Writing performance?"
              spaceName="IELTS Writing"
              humanCoachTitle="Talk to an IELTS coach"
              liwazaAITitle="Ask AI"
              communityTitle="Ask other students"
              showHumanCoach={true}
              showLiwazaAI={true}
              showCommunity={true}
              aifirstmessage={`I just completed an IELTS Writing test and got an overall band score of ${assessmentData?.overallBandScore?.toFixed(1) || 'N/A'}. I need help improving my writing performance.`}
              aicontext={`IELTS Writing Results: Overall Band Score: ${assessmentData?.overallBandScore?.toFixed(1) || 'N/A'}/9, Total Words: ${assessmentData?.totalWords || 0}. Task Performance: ${assessmentData?.tasks?.map((task, index) => `Task ${task.taskNumber}: ${task.estimatedBandScore?.toFixed(1) || 'N/A'}/9 (${task.wordCount} words)`).join(', ') || 'N/A'}.`}
            />
          </div>

          {/* Floating Action Buttons */}
          <div className="writing-floating-actions">
            <button
              className="writing-fab writing-share-btn"
              onClick={handleShare}
              title="Share Results"
            >
              <FiShare2 />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
